import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  IoArrowBack,
  IoPersonOutline,
  IoCalendarOutline,
  IoCheckmarkCircle,
  IoCloseCircle,
  IoAddCircleOutline,
  IoCloudUploadOutline,
  IoDocumentTextOutline,
  IoLocationOutline,
  IoCallOutline,
  IoMailOutline,
  IoMedkitOutline,
  IoTimeOutline,
  IoFolderOutline,
  IoDownloadOutline,
  IoChatbubblesOutline,
  IoStatsChartOutline,
} from 'react-icons/io5';
import UploadDocumentModal from './UploadDocumentModal';
import OrderDetailsModal from '../advancedcare/OrderDetailsModal';
import RequestReviewModal from './RequestReviewModal';
import Highlights from '../common/Highlights';
import PatientEditModal from './PatientEditModal';
import { IoPencilOutline } from 'react-icons/io5';
import { maskFullName } from '../../../utils/patientDisplay';
const API_BASE_URL = "https://promedhealth-frontdoor-h4c4bkcxfkduezec.z02.azurefd.net/api/v1/";

const getAuthHeaders = () => {
  const token = localStorage.getItem('accessToken');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

const PatientDetail = ({ patient, onBack, onAddOrder }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [patientOrders, setPatientOrders] = useState([]);
  const [patientIVRForms, setPatientIVRForms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [orderFilter, setOrderFilter] = useState('all'); // all, pending, approved, past
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [orderDetailsModalOpen, setOrderDetailsModalOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [patientDocuments, setPatientDocuments] = useState([]);
  const [requestReviewModalOpen, setRequestReviewModalOpen] = useState(false);
  const [editPatientModalOpen, setEditPatientModalOpen] = useState(false);

  useEffect(() => {
    if (patient) {
      loadPatientData();
    }
  }, [patient]);

  const loadPatientDocuments = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/patients/${patient.id}/documents/`, {
      headers: getAuthHeaders(),
    });
    const data = await response.json();
    if (data.success) {
      setPatientDocuments(data.documents);
    }
  } catch (err) {
    console.error('Error loading documents:', err);
  }
};

  const loadPatientData = async () => {
    setLoading(true);
    try {
      // Fetch patient's orders - use orders/recent endpoint
      const ordersResponse = await fetch(`${API_BASE_URL}/recent/?limit=100`, {
        headers: getAuthHeaders(),
      });
      const ordersData = await ordersResponse.json();
      
      // Filter orders for this specific patient
      const patientOrdersFiltered = ordersData.data?.filter(order => order.patient === patient.id) || [];
      setPatientOrders(patientOrdersFiltered);

      // Fetch patient's IVR forms
      const ivrResponse = await fetch(`${API_BASE_URL}/patients/${patient.id}/ivr-forms/`, {
        headers: getAuthHeaders(),
      });
      const ivrData = await ivrResponse.json();
      setPatientIVRForms(ivrData);
      await loadPatientDocuments();
    } catch (err) {
      console.error('Error loading patient data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Calculate patient status
  const isActive = patient.activate_Account === 'Activated';

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  };

  // Filter orders based on selected filter
  const filteredOrders = patientOrders.filter(order => {
    if (orderFilter === 'all') return true;
    if (orderFilter === 'pending') return order.status === 'pending';
    if (orderFilter === 'approved') return order.status === 'processing' || order.status === 'shipped';
    if (orderFilter === 'past') return order.status === 'delivered' || order.status === 'cancelled';
    return true;
  });

  // Tab configuration - REMOVED demographics
  const tabs = [
    { id: 'overview', label: 'Overview', icon: IoPersonOutline },
    { id: 'orders', label: 'Orders', icon: IoMedkitOutline, badge: patientOrders.length },
    { id: 'ivr', label: 'IVR Status', icon: IoDocumentTextOutline, badge: patientIVRForms.length },
    { id: 'notes', label: 'Clinical Review', icon: IoChatbubblesOutline },
    { id: 'folder', label: 'Patient Folder', icon: IoFolderOutline },
  ];

  // Render Overview Tab - UPDATED with 4 cards including Address
  const renderOverview = () => (
    <div className="space-y-6">
      {/* Info Cards Grid - Now 4 Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Patient Info Card */}
        <motion.div
          className="bg-gradient-to-br from-white via-teal-50/20 to-blue-50/10 dark:from-gray-800 dark:via-gray-800 dark:to-gray-800 rounded-2xl shadow-md border-2 border-teal-200/30 dark:border-teal-800/30 p-6 flex flex-col h-full hover:shadow-lg transition-shadow"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              <span className="text-white font-bold text-lg">
                {patient.first_name?.charAt(0)}{patient.last_name?.charAt(0)}
              </span>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Patient Info</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Basic details</p>
            </div>
          </div>
          
          <div className="space-y-4 flex-1">
            <div>
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                Full Name
              </p>
              <p className="text-base font-semibold text-gray-900 dark:text-white">
                {patient.first_name} {patient.last_name}
              </p>
            </div>
            
            <div>
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                Date of Birth
              </p>
              <p className="text-base font-semibold text-gray-900 dark:text-white">
                {formatDate(patient.date_of_birth)}
              </p>
            </div>
            
            {/* <div>
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                Medical Record #
              </p>
              <p className="text-base font-semibold text-gray-900 dark:text-white">
                N/A
              </p>
            </div> */}
          </div>
        </motion.div>

        {/* Contact Info Card */}
        <motion.div
          className="bg-gradient-to-br from-white via-teal-50/20 to-blue-50/10 dark:from-gray-800 dark:via-gray-800 dark:to-gray-800 rounded-2xl shadow-md border-2 border-teal-200/30 dark:border-teal-800/30 p-6 flex flex-col h-full hover:shadow-lg transition-shadow"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
              <IoCallOutline className="text-2xl text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Contact Info</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Communication</p>
            </div>
          </div>
          
          <div className="space-y-4 flex-1">
            <div>
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                Phone
              </p>
              <p className="text-base font-semibold text-gray-900 dark:text-white">
                {patient.phone_number || 'N/A'}
              </p>
            </div>
            
            <div>
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                Email
              </p>
              <p className="text-base font-semibold text-gray-900 dark:text-white break-all">
                {patient.email || 'N/A'}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Insurance Card */}
        <motion.div
          className="bg-gradient-to-br from-white via-teal-50/20 to-blue-50/10 dark:from-gray-800 dark:via-gray-800 dark:to-gray-800 rounded-2xl shadow-md border-2 border-teal-200/30 dark:border-teal-800/30 p-6 flex flex-col h-full hover:shadow-lg transition-shadow"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
              <IoMedkitOutline className="text-2xl text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Insurance</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Coverage info</p>
            </div>
          </div>
          
          <div className="space-y-4 flex-1">
            {/* Primary Insurance */}
            <div>
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                Primary Insurance
              </p>
              <p className="text-base font-semibold text-gray-900 dark:text-white">
                {patient.primary_insurance || 'N/A'}
              </p>
            </div>
            
            {/* Primary Insurance Number - FIXED FIELD NAME */}
            {patient.primary_insurance_number && (
              <div>
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                  Primary Policy #
                </p>
                <p className="text-base font-semibold text-gray-900 dark:text-white">
                  {patient.primary_insurance_number}
                </p>
              </div>
            )}
            
            {/* Secondary Insurance - Only show if exists */}
            {patient.secondary_insurance && (
              <>
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                    Secondary Insurance
                  </p>
                  <p className="text-base font-semibold text-gray-900 dark:text-white">
                    {patient.secondary_insurance}
                  </p>
                </div>
                
                {/* Secondary Insurance Number - FIXED FIELD NAME */}
                {patient.secondary_insurance_number && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                      Secondary Policy #
                    </p>
                    <p className="text-base font-semibold text-gray-900 dark:text-white">
                      {patient.secondary_insurance_number}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </motion.div>


        {/* Address Card - NEW */}
        <motion.div
          className="bg-gradient-to-br from-white via-teal-50/20 to-blue-50/10 dark:from-gray-800 dark:via-gray-800 dark:to-gray-800 rounded-2xl shadow-md border-2 border-teal-200/30 dark:border-teal-800/30 p-6 flex flex-col h-full hover:shadow-lg transition-shadow"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
              <IoLocationOutline className="text-2xl text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Address</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Location</p>
            </div>
          </div>
          
          <div className="space-y-4 flex-1">
            <div>
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                Street
              </p>
              <p className="text-base font-semibold text-gray-900 dark:text-white">
                {patient.address || 'N/A'}
              </p>
            </div>
            
            <div>
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                City, State
              </p>
              <p className="text-base font-semibold text-gray-900 dark:text-white">
                {patient.city && patient.state 
                  ? `${patient.city}, ${patient.state}`
                  : patient.city || patient.state || 'N/A'}
              </p>
            </div>
            
            <div>
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                Zip Code
              </p>
              <p className="text-base font-semibold text-gray-900 dark:text-white">
                {patient.zip_code || 'N/A'}
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Wound Details & Statistics - Full Width Below */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Wound Details Card */}
        {/* <motion.div
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
              <span className="text-2xl">🩹</span>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Wound Details</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Current measurements</p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                Type
              </p>
              <p className="text-base font-semibold text-gray-900 dark:text-white">
                {patient.wound_type || 'N/A'}
              </p>
            </div>
            
            <div>
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                Location
              </p>
              <p className="text-base font-semibold text-gray-900 dark:text-white">
                {patient.wound_location || 'N/A'}
              </p>
            </div>
            
            <div>
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                Size (L × W × D)
              </p>
              <p className="text-base font-semibold text-gray-900 dark:text-white">
                {patient.wound_size_length && patient.wound_size_width
                  ? `${patient.wound_size_length} × ${patient.wound_size_width} × ${patient.wound_size_depth || '0'} cm`
                  : 'N/A'}
              </p>
            </div>
            
            <div>
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                Drainage
              </p>
              <p className="text-base font-semibold text-gray-900 dark:text-white capitalize">
                {patient.wound_drainage || 'N/A'}
              </p>
            </div>
          </div>
        </motion.div>

        Statistics Card
        <motion.div
          className="bg-gradient-to-br from-cyan-50 to-teal-50 dark:from-cyan-900/20 dark:to-teal-900/20 rounded-2xl shadow-lg border-2 border-cyan-200 dark:border-cyan-800 p-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center">
              <IoStatsChartOutline className="text-2xl text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Statistics</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">Activity summary</p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-6">
            <div className="text-center">
              <div className="text-4xl font-black text-transparent bg-gradient-to-r from-cyan-600 to-teal-600 bg-clip-text mb-2">
                {patientOrders.length}
              </div>
              <p className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                Total Orders
              </p>
            </div>
            
            <div className="text-center">
              <div className="text-4xl font-black text-transparent bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text mb-2">
                {patientIVRForms.length}
              </div>
              <p className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                IVR Submissions
              </p>
            </div>
          </div>
        </motion.div> */}
      </div>
    </div>
  );

  // Render Orders Tab
  const renderOrders = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      {/* Filter Buttons */}
      <div className="mb-6 flex gap-3">
        {[
          { id: 'all', label: 'All' },
          { id: 'pending', label: 'Pending' },
          { id: 'approved', label: 'Approved' },
          { id: 'past', label: 'Past' },
        ].map((filter) => (
          <motion.button
            key={filter.id}
            onClick={() => setOrderFilter(filter.id)}
            className={`px-6 py-2 rounded-lg font-semibold transition-all ${
              orderFilter === filter.id
                ? 'bg-teal-500 text-white shadow-lg'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {filter.label}
          </motion.button>
        ))}
      </div>

      {/* Orders Table */}
      <div className="bg-gradient-to-br from-white via-teal-50/20 to-blue-50/10 dark:from-gray-800 dark:via-gray-800 dark:to-gray-800 rounded-2xl shadow-md border-2 border-teal-200/30 dark:border-teal-800/30 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase">Order #</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase">Kit</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase">Status</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-12 text-center">
                    <Highlights emoji="📦" color="blue" variant="card" emojiSize="text-5xl">
                      <p className="text-gray-500 dark:text-gray-400 font-semibold">No orders found</p>
                    </Highlights>
                  </td>
                </tr>
              ) : (
                filteredOrders.map((order) => (
                  <motion.tr
                    key={order.id}
                    onClick={() => {
                      setSelectedOrderId(order.id);
                      setOrderDetailsModalOpen(true);
                    }}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors cursor-pointer"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    <td className="px-6 py-4">
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">{order.order_number}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{order.kit_size_display || 'N/A'}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-lg text-xs font-semibold ${
                        order.status === 'pending' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300' :
                        order.status === 'processing' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300' :
                        order.status === 'shipped' ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300' :
                        order.status === 'delivered' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' :
                        'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                      }`}>
                        {order.status_display}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-600 dark:text-gray-400">{new Date(order.created_at).toLocaleDateString()}</span>
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </motion.div>
  );

  // Render IVR Status Tab
  const renderIVRStatus = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="bg-gradient-to-br from-white via-teal-50/20 to-blue-50/10 dark:from-gray-800 dark:via-gray-800 dark:to-gray-800 rounded-2xl shadow-md border-2 border-teal-200/30 dark:border-teal-800/30 overflow-hidden">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">IVR Submissions</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">Insurance verification requests</p>
        </div>
        
        {patientIVRForms.length === 0 ? (
          <div className="p-12 text-center">
            <Highlights emoji="📄" color="indigo" variant="card" emojiSize="text-6xl">
              <p className="text-gray-500 dark:text-gray-400 font-semibold text-lg">No IVR forms submitted yet</p>
            </Highlights>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {patientIVRForms.map((ivr) => (
              <motion.div
                key={ivr.id}
                className="p-6 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <span className={`px-4 py-1.5 rounded-full text-xs font-bold ${
                        ivr.status === 'approved' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' :
                        ivr.status === 'pending' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300' :
                        ivr.status === 'denied' ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300' :
                        'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                      }`}>
                        {ivr.status_display}
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {new Date(ivr.submitted_at).toLocaleDateString()}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase">Physician</p>
                        <p className="text-gray-900 dark:text-white font-medium">{ivr.physician_name || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase">Contact</p>
                        <p className="text-gray-900 dark:text-white font-medium">{ivr.contact_name || 'N/A'}</p>
                      </div>
                    </div>

                    {ivr.admin_notes && (
                      <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                        <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-1">Admin Notes</p>
                        <p className="text-sm text-gray-700 dark:text-gray-300">{ivr.admin_notes}</p>
                      </div>
                    )}
                  </div>

                  {ivr.pdf_url && (
                    <motion.a
                      href={ivr.pdf_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-4 px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg font-semibold text-sm flex items-center gap-2"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <IoDocumentTextOutline className="text-lg" />
                      View PDF
                    </motion.a>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );

  // Render Notes Tab
  // Render Notes Tab - Updated to show documents
// Render Notes Tab - Updated: adds AI + Medical Director callout + CTA
const renderNotes = () => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="space-y-6"
  >
    {/* NEW: AI + Medical Director Review Callout */}
    <motion.div
      className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 border-2 border-indigo-200 dark:border-indigo-800 rounded-2xl p-6"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.05 }}
    >
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-start gap-4">
          <motion.div
            className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full flex items-center justify-center flex-shrink-0"
            animate={{ rotate: [0, 8, -8, 0] }}
            transition={{ duration: 3, repeat: Infinity }}
          >
            <IoChatbubblesOutline className="text-3xl text-white" />
          </motion.div>

          <div className="text-left">
            <h3 className="text-sm font-black text-gray-900 dark:text-white uppercase tracking-wide mb-1">
              Clinical Review (AI + Medical Director)
            </h3>
            <p className="text-sm text-gray-700 dark:text-gray-300 font-medium">
              Have AI review your notes and match them to the LCD, then have a Medical Director review them as well.
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
              Upload your documents below, then request a review when ready.
            </p>
          </div>
        </div>

        <motion.button
          onClick={() => setRequestReviewModalOpen(true)}
          className="px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white rounded-xl font-bold shadow-lg flex items-center justify-center gap-2"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <IoDocumentTextOutline className="text-xl" />
          Request Review
        </motion.button>
      </div>
    </motion.div>

    {/* Header with Upload Button */}
    <div className="flex items-center justify-between">
      <div>
        <h3 className="text-2xl font-bold text-gray-900 dark:text-white">Patient Documents</h3>
        <p className="text-gray-600 dark:text-gray-400">Medical records, prescriptions, and notes</p>
      </div>
      <motion.button
        onClick={() => setUploadModalOpen(true)}
        className="px-6 py-3 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white rounded-xl font-bold shadow-lg flex items-center gap-2"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <IoCloudUploadOutline className="text-xl" />
        Upload Document
      </motion.button>
    </div>

    {/* Documents List */}
    {patientDocuments.length === 0 ? (
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
        <Highlights emoji="📄" color="indigo" variant="card" emojiSize="text-6xl">
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">No Documents Yet</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Upload medical records, prescriptions, or other documents
          </p>
        </Highlights>
        <motion.button
          onClick={() => setUploadModalOpen(true)}
          className="px-8 py-4 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white rounded-2xl font-bold shadow-lg flex items-center gap-3 mx-auto"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <IoCloudUploadOutline className="text-2xl" />
          Upload First Document
        </motion.button>
      </div>
    ) : (
      <div className="grid grid-cols-1 gap-4">
        {patientDocuments.map((doc) => (
          <motion.div
            key={doc.id}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 hover:shadow-xl transition-shadow"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4 flex-1">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center">
                  <IoDocumentTextOutline className="text-2xl text-white" />
                </div>
                <div className="flex-1">
                  <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
                    {doc.title}
                  </h4>
                  <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400 mb-2">
                    <span className="px-3 py-1 bg-teal-100 dark:bg-teal-900/30 text-teal-800 dark:text-teal-300 rounded-full font-semibold">
                      {doc.document_type_display}
                    </span>
                    <span>{new Date(doc.uploaded_at).toLocaleDateString()}</span>
                    <span>•</span>
                    <span>{doc.file_size_mb} MB</span>
                  </div>
                  {doc.notes && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">{doc.notes}</p>
                  )}
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">
                    Uploaded by {doc.uploaded_by_name}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <motion.a
                  href={doc.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg font-semibold text-sm flex items-center gap-2"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <IoDownloadOutline className="text-lg" />
                  View
                </motion.a>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    )}
  </motion.div>
);


  // Render Patient Folder Tab
  const renderPatientFolder = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
        <Highlights emoji="📁" color="purple" variant="card" emojiSize="text-6xl">
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Patient Folder</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">Download all patient documents and records</p>
        </Highlights>
        <motion.button
          className="px-8 py-4 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white rounded-2xl font-bold shadow-lg flex items-center gap-3 mx-auto"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <IoDownloadOutline className="text-2xl" />
          Download All Documents
        </motion.button>
        <p className="text-sm text-gray-500 dark:text-gray-500 mt-4">Coming soon</p>
      </div>
    </motion.div>
  );

  return (
    <div className="w-full bg-gradient-to-br from-gray-50 via-teal-50/30 to-blue-50/20 dark:from-gray-900 dark:via-gray-900 dark:to-gray-900 min-h-screen">
      <div className="px-4 sm:px-6 lg:px-12 pb-8 pt-6">
        {/* Back Button */}
        <motion.button
          onClick={onBack}
          className="flex items-center gap-2 text-teal-600 dark:text-teal-400 hover:gap-3 transition-all font-bold mb-6 group"
          whileHover={{ x: -5 }}
        >
          <IoArrowBack className="text-xl group-hover:scale-110 transition-transform" />
          Back to Patients
        </motion.button>

        {/* Header Section */}
        <motion.div
          className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl border border-gray-200 dark:border-gray-700 p-8 mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            {/* Patient Info */}
            <div className="flex items-center gap-6">
              <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-full flex items-center justify-center text-white font-black text-3xl shadow-lg">
                {patient.first_name?.charAt(0)}{patient.last_name?.charAt(0)}
              </div>
              <div>
                <div className="flex items-center gap-4 mb-2">
                  <h1 className="text-3xl font-black text-gray-900 dark:text-white">
                    {patient.first_name} {patient.last_name}
                  </h1>
                  <span className={`px-4 py-1.5 rounded-full text-sm font-bold flex items-center gap-2 ${
                    isActive
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                      : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                  }`}>
                    {isActive ? (
                      <>
                        <IoCheckmarkCircle className="text-lg" />
                        Active
                      </>
                    ) : (
                      <>
                        <IoCloseCircle className="text-lg" />
                        Inactive
                      </>
                    )}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-gray-600 dark:text-gray-400">
                  <div className="flex items-center gap-2">
                    <IoCalendarOutline />
                    <span className="font-semibold">DOB: {formatDate(patient.date_of_birth)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3">
              <motion.button
                onClick={() => onAddOrder && onAddOrder(patient)}
                className="px-6 py-3 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white rounded-xl font-bold shadow-lg flex items-center gap-2"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <IoAddCircleOutline className="text-xl" />
                Add Order
              </motion.button>
              <motion.button
                onClick={() => setUploadModalOpen(true)}
                className="px-6 py-3 bg-white dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-xl font-bold flex items-center gap-2"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <IoCloudUploadOutline className="text-xl" />
                Upload Docs
              </motion.button>
              <motion.button
                onClick={() => setEditPatientModalOpen(true)}
                className="px-6 py-3 bg-white dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-xl font-bold flex items-center gap-2"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <IoPencilOutline className="text-xl" />
                Edit Patient
              </motion.button>
              <motion.button
  onClick={() => setRequestReviewModalOpen(true)}
  className="px-6 py-3 bg-white dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-xl font-bold flex items-center gap-2"
  whileHover={{ scale: 1.05 }}
  whileTap={{ scale: 0.95 }}
>
  <IoDocumentTextOutline className="text-xl" />
  Request Review
</motion.button>
            </div>
          </div>
        </motion.div>

        {/* Tabs */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 mb-6">
          <div className="flex overflow-x-auto">
            {tabs.map((tab) => (
              <motion.button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 min-w-[150px] px-6 py-4 font-bold text-sm transition-all relative ${
                  activeTab === tab.id
                    ? 'text-teal-600 dark:text-teal-400'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
                whileHover={{ y: -2 }}
              >
                <div className="flex items-center justify-center gap-2">
                  <tab.icon className="text-xl" />
                  <span>{tab.label}</span>
                  {tab.badge !== undefined && tab.badge > 0 && (
                    <span className="ml-1 px-2 py-0.5 bg-teal-500 text-white rounded-full text-xs font-bold">
                      {tab.badge}
                    </span>
                  )}
                </div>
                {activeTab === tab.id && (
                  <motion.div
                    className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-500 to-cyan-500"
                    layoutId="activeTab"
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  />
                )}
              </motion.button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {activeTab === 'overview' && renderOverview()}
            {activeTab === 'orders' && renderOrders()}
            {activeTab === 'ivr' && renderIVRStatus()}
            {activeTab === 'notes' && renderNotes()}
            {activeTab === 'folder' && renderPatientFolder()}
          </motion.div>
        </AnimatePresence>
      </div>

      <RequestReviewModal
        open={requestReviewModalOpen}
        onClose={() => setRequestReviewModalOpen(false)}
        patient={patient}
        onSuccess={(reviewRequest) => {
          console.log('Review request created:', reviewRequest);
          // Optionally reload patient data or show notification
        }}
      />

      {/* Order Details Modal */}
      <OrderDetailsModal
        open={orderDetailsModalOpen}
        onClose={() => {
          setOrderDetailsModalOpen(false);
          setSelectedOrderId(null);
        }}
        orderId={selectedOrderId}
      />

      {/* Upload Document Modal */}
      <UploadDocumentModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        patient={patient}
        onUploadSuccess={() => {
          loadPatientDocuments(); // Reload documents after upload
        }}
      />
      <PatientEditModal
  open={editPatientModalOpen}
  onClose={() => setEditPatientModalOpen(false)}
  patient={patient}
  onSuccess={(updatedPatient) => {
    window.location.reload(); // Or update patient state
  }}
/>
    </div>
  );
};

export default PatientDetail;